import { useFormik } from 'formik';
import type { FormikHelpers } from 'formik/dist/types';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { getCurrentUser } from '@app/redux/auth/auth.selectors';

import authAppTOTPValidationScheme from '@components/forms/verifyTOTP/validationScheme';

import downloadDocuments from '@utils/downloadDocuments';
import formatDate from '@utils/formatDate';

export interface AuthAppTOTPFormValues {
	code: string;
}

function useAuthAppTOTPForm() {
	const { startRequest: addTOTPMethod } = useTriggeredRequest(AccountClientRequests.MFA.addTOTPMethod);
	const { startRequest: enable2FA } = useTriggeredRequest(AccountClientRequests.MFA.toggleMFA);
	const [backupCodes, setBackupCodes] = useState<string[]>();
	const currentUser = useSelector(getCurrentUser);

	const formik = useFormik<AuthAppTOTPFormValues>({
		validationSchema: authAppTOTPValidationScheme,
		initialValues: { code: '' },
		onSubmit: async (values: AuthAppTOTPFormValues, formikHelpers: FormikHelpers<AuthAppTOTPFormValues>) => {
			try {
				const res = await addTOTPMethod({ name: `TOTP_${currentUser?.email}_${Date.now()}`, code: values.code });
				setBackupCodes(res.backup_codes);
			} catch (error) {
				formikHelpers.setErrors({ code: 'Oops. Something went wrong.' });
			}
		},
	});

	const handleDownloadFile = () => {
		if (backupCodes) {
			downloadDocuments(
				{ fileStream: backupCodes.map(code => code).join('\n'), contentType: 'text/plain' },
				`Backup codes(${formatDate(new Date())}).txt`,
			);
		}
	};

	const handleEnableTOTP2FA = () => enable2FA({ mfa_enabled: true });

	return { handleDownloadFile, handleEnableTOTP2FA, backupCodes, formik };
}

export default useAuthAppTOTPForm;
